import * as React from "react";
import { ChartData, formatXLabel, GroupBy, OnMouseMoveParam, PageView, TimeStep } from "./utils";
import { Card } from "antd";
import { ZoomIn } from "react-feather";

interface Props {
  data: OnMouseMoveParam | null;
  timeStep: TimeStep;
  groupBy: GroupBy;
  pageView: PageView;
  chartData: ChartData;
  drillDown: any;
}
export const CardTooltip: React.FC<Props> = ({ data, timeStep, groupBy, pageView, chartData, drillDown }) => {
  const hasOrg = React.useMemo(() => {
    return data?.activePayload?.some((p) => {
      if (groupBy === "org") {
        return false;
      }
      if (groupBy === "observability") {
        return false;
      }

      const payloadDataKey = p.dataKey
        .replace("primaryTotalMapForDisplay.", "")
        .replace("secondaryTotalMapForDisplay.", "")
        .replace("valueMapForDisplay.", "")
        .replace("projectedTotalMapForDisplay.", "")
        .replace("projectedValueMapForDisplay.", "");
      const unit = chartData.units.find((unit) => unit.dataKey === payloadDataKey);
      return !!unit?.org;
    });
  }, [data]);

  const hasGVC = React.useMemo(() => {
    return data?.activePayload?.some((p) => {
      if (groupBy === "gvc") {
        return false;
      }
      const payloadDataKey = p.dataKey
        .replace("primaryTotalMapForDisplay.", "")
        .replace("secondaryTotalMapForDisplay.", "")
        .replace("valueMapForDisplay.", "")
        .replace("projectedTotalMapForDisplay.", "")
        .replace("projectedValueMapForDisplay.", "");
      const unit = chartData.units.find((unit) => unit.dataKey === payloadDataKey);
      return !!unit?.gvc;
    });
  }, [data]);

  const hasAnyProjection = React.useMemo(() => {
    return data?.activePayload?.some((p) => {
      if (!p.dataKey.toLowerCase().startsWith("projected")) {
        return false;
      }
      if (!p?.value) {
        return false;
      }
      return p.value > 0.01;
    });
  }, [data]);

  const payloadsToShow = React.useMemo(() => {
    return (data?.activePayload || [])
      .filter((p) => !p.dataKey.includes("projected"))
      .filter((p) => p.dataKey !== "cpln-placeholder-hover")
      .sort((a, b) => {
        const aValue = a.value || 0;
        const bValue = b.value || 0;
        if (aValue > bValue) return -1;
        if (aValue < bValue) return 1;
        return 0;
      });
  }, [data]);

  const canDrillDown = React.useMemo(() => {
    if (timeStep === "hour") return false;
    const label = data?.activeLabel;
    if (!label) return false;
    return true;
  }, [data, timeStep]);

  const left = React.useMemo(() => {
    if (!data) {
      return 0;
    }
    if (data.activeCoordinate.x > 1310 / 2) {
      return undefined;
    }
    return data.activeCoordinate.x - 10;
  }, [data]);
  const right = React.useMemo(() => {
    if (!data) {
      return undefined;
    }
    if (left !== undefined) {
      return undefined;
    }
    return 1310 - data.activeCoordinate.x - 10;
  }, [data, left]);
  const transition = React.useMemo(() => {
    if (!!left) {
      return "left 0.3s ease-in-out";
    }
    return "right 0.3s ease-in-out";
  }, [data, left, right]);

  const label = React.useMemo(() => {
    return formatXLabel({ value: data?.activeLabel || "", timeStep: timeStep });
  }, [data, timeStep]);

  if (!data || !data.activePayload || !data.activePayload.length) {
    return null;
  }

  return (
    <Card
      className="card-tooltip absolute overflow-y-scroll color-default"
      styles={{
        body: { padding: 0 },
      }}
      style={{
        maxHeight: 500,
        left: left,
        right: right,
        top: 300,
        transition: transition,
      }}
    >
      <div
        className={`rounded border p-2 bg-layout ${payloadsToShow.length > 30 ? "text-xs" : ""}`}
        style={{ backgroundColor: "var(--color-drop)" }}
      >
        <div className={`mb-2 flex flex-col`}>
          <div className="flex items-center gap-2">
            <span>{label}</span>
            {canDrillDown ? (
              <button onClick={drillDown} className="color-link">
                <ZoomIn className="feather-icon" />
              </button>
            ) : null}
          </div>
          <span>{pageView === "cost" ? null : <span className="text-xs">{chartData.metricUnit}</span>}</span>
        </div>
        <div className="flex gap-4">
          <div>
            <div className="font-semibold">Name</div>
            {payloadsToShow.map((p) => {
              return (
                <div key={label + p.dataKey} style={{ color: p.fill }}>
                  <span>{formatName(chartData, p.dataKey)}</span>
                </div>
              );
            })}
          </div>
          {hasOrg ? (
            <div>
              <div className="font-semibold">Org</div>
              {payloadsToShow.map((p) => {
                const text = formatOrg(chartData, p.dataKey);
                return (
                  <div
                    key={label + p.dataKey}
                    style={{ color: p.fill, visibility: text === "none" ? "hidden" : "visible" }}
                  >
                    <span>{text}</span>
                  </div>
                );
              })}
            </div>
          ) : null}
          {hasGVC ? (
            <div>
              <div className="font-semibold">Gvc</div>
              {payloadsToShow.map((p) => {
                const text = formatGvc(chartData, p.dataKey);
                return (
                  <div
                    key={label + p.dataKey}
                    style={{ color: p.fill, visibility: text === "none" ? "hidden" : "visible" }}
                  >
                    <span>{text}</span>
                  </div>
                );
              })}
            </div>
          ) : null}
          <div>
            <div className="font-semibold">{pageView === "cost" ? "Cost" : "Value"}</div>
            {payloadsToShow.map((p) => {
              return (
                <div key={label + p.dataKey} style={{ color: p.fill }}>
                  <span>{p.unit}</span>
                  <span style={{ color: p.fill }}>{getValue(data?.activePayload, p.dataKey)}</span>
                </div>
              );
            })}
          </div>
          {hasAnyProjection ? (
            <div>
              <div className="font-semibold">Projected {pageView === "cost" ? "Cost" : "Value"}</div>
              {payloadsToShow.map((p) => {
                const text = getProjectedValue(data?.activePayload, pageView, p.dataKey);
                return (
                  <div
                    key={label + p.dataKey}
                    style={{ color: p.fill, visibility: text === "none" ? "hidden" : "visible" }}
                  >
                    {text}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
};

function formatName(chartData: ChartData, dataKey: string) {
  dataKey = dataKey
    .replace("primaryTotalMapForDisplay.", "")
    .replace("secondaryTotalMapForDisplay.", "")
    .replace("valueMapForDisplay.", "")
    .replace("projectedTotalMapForDisplay.", "")
    .replace("projectedValueMapForDisplay.", "");
  const unit = chartData.units.find((unit) => unit.dataKey === dataKey);

  return unit?.label || dataKey;
}

function formatGvc(chartData: ChartData, dataKey: string) {
  dataKey = dataKey
    .replace("primaryTotalMapForDisplay.", "")
    .replace("secondaryTotalMapForDisplay.", "")
    .replace("valueMapForDisplay.", "")
    .replace("projectedTotalMapForDisplay.", "")
    .replace("projectedValueMapForDisplay.", "");
  const unit = chartData.units.find((unit) => unit.dataKey === dataKey);

  // keep none here, it is compared and rendered differently
  return unit?.gvc || "none";
}

function formatOrg(chartData: ChartData, dataKey: string) {
  dataKey = dataKey
    .replace("primaryTotalMapForDisplay.", "")
    .replace("secondaryTotalMapForDisplay.", "")
    .replace("valueMapForDisplay.", "")
    .replace("projectedTotalMapForDisplay.", "")
    .replace("projectedValueMapForDisplay.", "");
  const unit = chartData.units.find((unit) => unit.dataKey === dataKey);

  // keep none here, it is compared and rendered differently
  return unit?.org || "none";
}

function getValue(payload: any[], dataKey: string) {
  const main = payload?.find((p) => p.dataKey === dataKey)!;
  let text = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    main.value || 0,
  );
  return text;
}

function getProjectedValue(payload: any[], pageView: PageView, dataKey: string) {
  const main = payload?.find((p) => p.dataKey === dataKey)!;
  let projectedDataKey = dataKey;
  if (pageView === "metric") {
    projectedDataKey = dataKey.replace("valueMapForDisplay", `projectedValueMapForDisplay`);
  } else {
    if (dataKey.startsWith("secondary")) {
      projectedDataKey = dataKey.replace(`secondaryTotalMapForDisplay`, `projectedTotalMapForDisplay`);
    } else if (dataKey.startsWith("primary")) {
      projectedDataKey = dataKey.replace(`primaryTotalMapForDisplay`, `projectedTotalMapForDisplay`);
    }
  }
  const projected = payload?.find((p) => p.dataKey === projectedDataKey);

  if (projected && projected.value > 0.01) {
    const projectedText = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(main.value + projected.value);
    return `${projected.unit}${projectedText}`;
  }

  return "none";
}
